import { useSearchParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import Pagination from '@/components/base/Pagination';
import { movies } from '@/mocks/movies';
import { sortOptions, pageSize } from '@/constants/filters';
import { usePageMeta } from '@/hooks/usePageMeta';
import FilterSidebar from './components/FilterSidebar';
import MovieListItem from './components/MovieListItem';

export default function Category() {
  usePageMeta(
    '影视分类 - 玉女心经2',
    '按类型、年代、地区筛选影视作品，支持按更新时间与点击量排序，帮你快速找到想了解的影视资料与角色信息。'
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const genre = searchParams.get('genre') ?? '全部';
  const year = searchParams.get('year') ?? '全部';
  const region = searchParams.get('region') ?? '全部';
  const sort = searchParams.get('sort') ?? 'updated';
  const page = Number(searchParams.get('page') ?? '1') || 1;

  let list = movies.filter(
    (m) =>
      (genre === '全部' || m.genres.includes(genre)) &&
      (year === '全部' || String(m.year) === year) &&
      (region === '全部' || m.region === region)
  );
  list = [...list].sort((a, b) =>
    sort === 'views' ? b.views - a.views : b.updatedAt.localeCompare(a.updatedAt)
  );

  const totalPages = Math.max(1, Math.ceil(list.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageItems = list.slice((safePage - 1) * pageSize, safePage * pageSize);

  const update = (key: 'genre' | 'year' | 'region', value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === '全部') next.delete(key);
    else next.set(key, value);
    next.delete('page');
    setSearchParams(next);
  };

  const updateSort = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === 'updated') next.delete('sort');
    else next.set('sort', value);
    next.delete('page');
    setSearchParams(next);
  };

  const goPage = (p: number) => {
    const next = new URLSearchParams(searchParams);
    if (p <= 1) next.delete('page');
    else next.set('page', String(p));
    setSearchParams(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const resetAll = () => setSearchParams({});

  const heading = genre === '全部' ? '全部影视作品' : `${genre} · 影视作品`;

  return (
    <div className="w-full bg-background-50 px-4 md:px-8 py-8 md:py-12">
      <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '影视分类' }]} />

      <div className="mt-5 max-w-3xl">
        <h1 className="font-heading text-2xl font-black text-foreground-950 md:text-4xl">影视分类</h1>
        <p className="mt-3 text-sm leading-7 text-foreground-600">
          这里是玉女心经2 影视内容站的资料总入口。按类型、年代、地区组合筛选，再按更新时间或点击量排序，逐步缩小范围，找到你想深入了解的作品与角色。
        </p>
      </div>

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-4">
        <aside className="lg:col-span-1">
          <div className="lg:sticky lg:top-24 space-y-4">
            <FilterSidebar genre={genre} year={year} region={region} onChange={update} />
            <button
              type="button"
              onClick={resetAll}
              className="flex w-full items-center justify-center gap-2 rounded-md border border-background-300/70 bg-background-50 px-4 py-2.5 font-label text-sm text-foreground-700 transition-colors hover:border-accent-300 hover:text-accent-700 cursor-pointer"
            >
              <i className="ri-refresh-line"></i> 重置全部筛选
            </button>
          </div>
        </aside>

        <div className="lg:col-span-3">
          <div className="flex flex-col gap-4 rounded-lg border border-background-200/70 bg-background-100 p-4 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="font-heading text-lg font-bold text-foreground-950">{heading}</h2>
            <div className="flex items-center gap-3">
              <span className="hidden font-label text-xs text-foreground-500 sm:inline">
                共 {list.length} 部
              </span>
              <div className="flex items-center rounded-full bg-background-200/70 px-1 py-1">
                {sortOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => updateSort(opt.value)}
                    className={`rounded-full px-3 py-1.5 font-label text-xs transition-colors cursor-pointer ${
                      sort === opt.value
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-600 hover:text-primary-700'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {pageItems.length > 0 ? (
            <>
              <div className="mt-6 space-y-4">
                {pageItems.map((movie) => (
                  <MovieListItem key={movie.id} movie={movie} />
                ))}
              </div>
              <Pagination current={safePage} total={totalPages} onChange={goPage} />
            </>
          ) : (
            <div className="mt-6 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300/80 bg-background-100 px-6 py-16 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary-100 text-secondary-500">
                <i className="ri-inbox-archive-line text-3xl"></i>
              </span>
              <h3 className="mt-4 font-heading text-lg font-bold text-foreground-900">没有匹配的影视作品</h3>
              <p className="mt-2 max-w-sm text-sm text-foreground-600">
                当前筛选条件下暂无内容，试试放宽条件或直接重置，浏览全部作品。
              </p>
              <button
                type="button"
                onClick={resetAll}
                className="mt-5 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
              >
                <i className="ri-refresh-line"></i> 重置筛选条件
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}